import pick from 'lodash/pick';

import {and, any} from '@edna/utils';

import {TItemId} from 'src/constants';
import {TTenantLeadDetailsPayload} from 'src/containers/LeadCompanyClients';
import {EPermission} from 'src/containers/User';
import {rootApi} from 'src/models';

import {EResidentCountry, TCompanyDetails} from './definitions';
import {getResidentCountry} from './utils';

const commonFields = [
  'country',
  'taxNumber',
  'companyName',
  'legalAddress',
] as unknown as keyof TCompanyDetails;

export const prepareCompanyData = (data: TCompanyDetails) => {
  const residentCountry = getResidentCountry(data.country);

  switch (residentCountry) {
    case EResidentCountry.RUS:
      return pick(data, [...commonFields, 'ogrn', 'kpp', 'siteAddress', 'postAddress']);
    case EResidentCountry.BLR:
      return pick(data, [...commonFields, 'siteAddress', 'postAddress']);
    case EResidentCountry.KAZ:
      return pick(data, [...commonFields, 'postAddress']);
    case EResidentCountry.FOREIGN:
    default:
      return pick(data, [...commonFields, 'ogrn']);
  }
};

const api = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    getCompanyDetails: builder.query<TCompanyDetails, TItemId | void>({
      query: (supportTenantId) => ({
        method: 'GET',
        url:
          supportTenantId !== undefined
            ? `/tenantmanagement/tenant/${supportTenantId}/company-details`
            : '/tenantmanagement/tenant/company-details',
        meta: {
          isShowError: true,
          permissions:
            supportTenantId !== undefined
              ? and<EPermission>('PERMISSION_TENANT_DETAILS_READ')
              : any(),
        },
      }),
      transformResponse: (data: TCompanyDetails) => prepareCompanyData(data) as TCompanyDetails,
    }),
    saveCompanyDetails: builder.mutation<
      TCompanyDetails,
      TTenantLeadDetailsPayload<TCompanyDetails>
    >({
      query: ({data, supportTenantId}) => ({
        method: 'POST',
        url:
          supportTenantId !== undefined
            ? `/tenantmanagement/tenant/${supportTenantId}/company-details`
            : '/tenantmanagement/tenant/company-details',
        data: prepareCompanyData(data),
        meta: {
          isShowError: true,
          permissions:
            supportTenantId !== undefined
              ? and<EPermission>('PERMISSION_TENANT_DETAILS_WRITE')
              : any(),
        },
      }),
      async onQueryStarted({supportTenantId}, {dispatch, queryFulfilled}) {
        try {
          const {data: newCompanyDetails} = await queryFulfilled;

          dispatch(
            api.util.updateQueryData('getCompanyDetails', supportTenantId, () => newCompanyDetails),
          );
        } catch {}
      },
    }),
  }),
});

export const {
  useGetCompanyDetailsQuery,
  useSaveCompanyDetailsMutation,
  endpoints: {getCompanyDetails},
} = api;
